/* MoonGadgets Store Redesign helpers */
(() => {
  'use strict';

  const debounce = (fn, wait = 350) => {
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => fn(...args), wait);
    };
  };

  const refreshSliderVisual = (slider) => {
    const minRange = slider.querySelector('[data-moon-range-min]');
    const maxRange = slider.querySelector('[data-moon-range-max]');
    if (!minRange || !maxRange) return;

    const min = Number(minRange.min || 0);
    const max = Number(minRange.max || maxRange.max || 100);
    const from = Number(minRange.value || min);
    const to = Number(maxRange.value || max);
    const span = Math.max(max - min, 1);

    slider.style.setProperty('--moon-range-from', `${((from - min) / span) * 100}%`);
    slider.style.setProperty('--moon-range-to', `${((to - min) / span) * 100}%`);
  };

  const initPriceSlider = (slider) => {
    if (!slider || slider.dataset.moonReady === 'true') return;

    const priceRange = slider.closest('price-range');
    if (!priceRange) return;

    const numberInputs = priceRange.querySelectorAll('input[type="number"]');
    const minField = numberInputs[0];
    const maxField = numberInputs[1];
    const minRange = slider.querySelector('[data-moon-range-min]');
    const maxRange = slider.querySelector('[data-moon-range-max]');
    if (!minField || !maxField || !minRange || !maxRange) return;

    slider.dataset.moonReady = 'true';

    const absoluteMin = Number(minRange.min || 0);
    const absoluteMax = Number(maxRange.max || minRange.max || 0);

    const syncFromFields = () => {
      let from = minField.value === '' ? absoluteMin : Number(minField.value);
      let to = maxField.value === '' ? absoluteMax : Number(maxField.value);
      if (!Number.isFinite(from)) from = absoluteMin;
      if (!Number.isFinite(to)) to = absoluteMax;
      from = Math.max(absoluteMin, Math.min(from, to));
      to = Math.min(absoluteMax, Math.max(to, from));
      minRange.value = String(from);
      maxRange.value = String(to);
      refreshSliderVisual(slider);
    };

    const submitFacet = debounce((field) => {
      field.dispatchEvent(new Event('input', { bubbles: true }));
    }, 450);

    minRange.addEventListener('input', () => {
      let from = Number(minRange.value);
      const to = Number(maxRange.value);
      if (from > to) {
        from = to;
        minRange.value = String(from);
      }
      minField.value = from <= absoluteMin ? '' : String(from);
      refreshSliderVisual(slider);
    });

    maxRange.addEventListener('input', () => {
      const from = Number(minRange.value);
      let to = Number(maxRange.value);
      if (to < from) {
        to = from;
        maxRange.value = String(to);
      }
      maxField.value = to >= absoluteMax ? '' : String(to);
      refreshSliderVisual(slider);
    });

    minRange.addEventListener('change', () => submitFacet(minField));
    maxRange.addEventListener('change', () => submitFacet(maxField));
    minField.addEventListener('change', syncFromFields);
    maxField.addEventListener('change', syncFromFields);

    syncFromFields();
  };

  const initAll = (root = document) => {
    root.querySelectorAll('[data-moon-price-slider]').forEach(initPriceSlider);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (!(node instanceof Element)) continue;
        if (node.matches('[data-moon-price-slider]')) initPriceSlider(node);
        initAll(node);
      }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });


  const closeMobileMenu = (details) => {
    if (!details) return;
    details.removeAttribute('open');
    document.documentElement.classList.remove('moon-mobile-menu-open');
  };

  document.addEventListener('toggle', (event) => {
    const details = event.target.closest?.('[data-moon-mobile-menu]');
    if (!details || event.target !== details) return;
    document.documentElement.classList.toggle('moon-mobile-menu-open', details.open);
  }, true);

  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-moon-menu-close]');
    if (closeButton) {
      event.preventDefault();
      closeMobileMenu(closeButton.closest('[data-moon-mobile-menu]'));
      return;
    }

    const focusButton = event.target.closest('[data-moon-search-focus]');
    if (focusButton) {
      event.preventDefault();
      const input = document.getElementById('MoonMobileSearchV4');
      input?.focus({ preventScroll: false });
      input?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      return;
    }

    const layoutButton = event.target.closest('.custom-product-grid [data-cols]');
    if (layoutButton) {
      const container = layoutButton.closest('#ProductGridContainer') || document;
      const grid = container.querySelector('ul#product-grid');
      if (!grid) return;
      const classes = [
        'list-view-filter', 'grid-view-filter',
        'grid--2-col-desktop', 'grid--3-col-desktop', 'grid--4-col-desktop',
        'grid--5-col-desktop', 'grid--6-col-desktop',
        'align-1-column', 'align-2-column', 'align-3-column', 'align-4-column',
        'align-5-column', 'align-6-column'
      ];
      grid.classList.remove(...classes);
      const cols = layoutButton.dataset.cols;
      const value = layoutButton.dataset.val || '4';
      if (cols === 'list-view-filter') {
        grid.classList.add('list-view-filter');
      } else {
        grid.classList.add('grid-view-filter');
        if (cols) grid.classList.add(cols);
      }
      grid.classList.add(`align-${value}-column`);
      layoutButton.parentElement?.querySelectorAll('[data-cols]').forEach((button) => {
        const active = button === layoutButton;
        button.classList.toggle('active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    const layoutButton = event.target.closest('.custom-product-grid [data-cols]');
    if (!layoutButton) return;
    event.preventDefault();
    layoutButton.click();
  });

})();
